import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { CreditCard, Check, AlertCircle, Shield } from '../../lib/lucide-proxy';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../config/supabase';
import { STRIPE_PRICES, PLAN_FEATURES } from '../../config/stripe';
import toast from 'react-hot-toast';

const Billing = () => {
  const { profile } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    // If admin, we don't need to fetch subscription strictly, or we can skip
    if (profile?.role === 'admin') {
      setLoading(false);
      return;
    }
    if (profile?.brewery) {
      fetchSubscription();
    }
  }, [profile]);

  const fetchSubscription = async () => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select(`*, plan:plans(*)`)
        .eq('brewery_id', profile.brewery.id)
        .maybeSingle();
        
      if (error) throw error;
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (priceId) => {
    setProcessing(true);
    const toastId = toast.loading('Iniciando checkout...');
    
    try {
      // Chamada para Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          priceId,
          customerId: profile.id, // ID do usuário 
          breweryId: profile.brewery.id,
          email: profile.email,
          returnUrl: window.location.origin + '/admin/billing' // Fixed URL (removed /#/)
        }
      });

      if (error) throw new Error(error.message || 'Erro ao conectar com servidor');
      if (data?.error) throw new Error(data.error);
      
      if (data?.url) {
        window.location.href = data.url; // Redireciona para o Stripe Hosted Checkout
      } else {
        throw new Error('URL de checkout não recebida');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Erro ao processar: ' + error.message, { id: toastId });
    } finally {
      setProcessing(false);
    }
  };

  const handleManageSubscription = async () => {
    setProcessing(true);
    const toastId = toast.loading('Abrindo portal...');
    
    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: {
          customerId: subscription.stripe_customer_id,
          returnUrl: window.location.origin + '/admin/billing' // Fixed URL (removed /#/)
        }
      });

      if (error) throw error;
      
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating portal session:', error);
      toast.error('Erro ao abrir portal', { id: toastId });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const isAdmin = profile?.role === 'admin';

  return (
    <>
      <Helmet>
        <title>Assinatura - Cerveja Delivery</title>
      </Helmet>
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-dark-900">Plano e Assinatura</h2>
        
        {/* Admin Override View */}
        {isAdmin ? (
          <div className="bg-dark-900 text-white rounded-xl p-8 shadow-lg border border-primary-500/50 relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider">
              Admin Access
            </div>
            <div className="flex items-center mb-4">
              <Shield className="w-8 h-8 text-primary-500 mr-4" />
              <div>
                <h3 className="text-2xl font-bold">Plano Profissional (Vitalício)</h3>
                <p className="text-gray-400">Administradores têm acesso irrestrito a todas as funcionalidades.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="flex items-center">
                <Check className="w-5 h-5 text-green-500 mr-2" />
                <span>Produtos Ilimitados</span>
              </div>
              <div className="flex items-center">
                <Check className="w-5 h-5 text-green-500 mr-2" />
                <span>Analytics Avançado</span>
              </div>
              <div className="flex items-center">
                <Check className="w-5 h-5 text-green-500 mr-2" />
                <span>Gestão de Blog</span>
              </div>
              <div className="flex items-center">
                <Check className="w-5 h-5 text-green-500 mr-2" />
                <span>API Bulk Upload</span>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Normal Brewery View */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <strong>Configuração Necessária:</strong> Para processar pagamentos reais, você precisa implantar as Edge Functions no Supabase.
              </div>
            </div>

            {/* Current Plan */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-lg font-bold text-dark-900 mb-2">Plano Atual</h3>
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl font-bold text-primary-500">
                      {subscription?.plan?.name || 'Plano Gratuito'}
                    </span>
                    {subscription?.plan?.price > 0 && (
                      <span className="text-lg text-dark-600">
                        R$ {subscription.plan.price}/ano
                      </span>
                    )}
                  </div>
                  <p className="text-dark-500 mt-2">
                    {subscription?.plan?.slug === 'pro' 
                      ? 'Você tem acesso total à plataforma.' 
                      : 'Faça o upgrade para desbloquear todos os recursos.'}
                  </p>
                </div>
                {subscription?.stripe_customer_id && (
                  <Button variant="outline" onClick={handleManageSubscription} loading={processing}>
                    Gerenciar Assinatura
                  </Button>
                )}
              </div>

              {/* Subscription Status Badge */}
              {subscription && (
                <div className="border-t border-gray-200 pt-4 flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${subscription.status === 'active' ? 'bg-green-500' : 'bg-yellow-500'}`} />
                  <span className="text-sm font-medium capitalize">{subscription.status}</span>
                  {subscription.current_period_end && (
                    <span className="text-sm text-gray-500">- Renova em {new Date(subscription.current_period_end).toLocaleDateString()}</span>
                  )}
                </div>
              )}
            </div>

            {/* Available Plans */}
            <div>
              <h3 className="text-lg font-bold text-dark-900 mb-6">Escolha seu Plano</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
                {/* Free Plan */}
                <div className={`bg-white rounded-2xl p-8 shadow-soft border-2 ${!subscription || subscription.plan?.slug === 'free' ? 'border-gray-200 opacity-75' : 'border-transparent'}`}>
                  <h3 className="text-2xl font-display font-bold text-dark-900 mb-2">
                    {PLAN_FEATURES.FREE.name}
                  </h3>
                  <div className="flex items-baseline mb-6">
                    <span className="text-4xl font-bold text-dark-900">{PLAN_FEATURES.FREE.price}</span>
                    <span className="text-dark-500 ml-2">/mês</span>
                  </div>
                  <ul className="space-y-4 mb-8">
                    {PLAN_FEATURES.FREE.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-dark-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant="outline" className="w-full" disabled>
                    {!subscription ? 'Plano Atual' : 'Disponível'}
                  </Button>
                </div>

                {/* Pro Plan */}
                <div className="bg-dark-900 rounded-2xl p-8 shadow-large border-2 border-primary-500 relative overflow-hidden">
                  <div className="absolute top-0 right-0 bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider">
                    Oferta Limitada
                  </div>
                  <h3 className="text-2xl font-display font-bold text-white mb-2">
                    {PLAN_FEATURES.PRO.name}
                  </h3>
                  <div className="flex items-baseline mb-6">
                    <span className="text-4xl font-bold text-white">R$ 100</span>
                    <span className="text-gray-400 ml-2">/ano</span>
                  </div>
                  <p className="text-gray-300 mb-8 text-sm">
                    Promoção especial para os primeiros parceiros da plataforma.
                  </p>
                  <ul className="space-y-4 mb-8">
                    {PLAN_FEATURES.PRO.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-200">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {subscription?.plan?.slug === 'pro' ? (
                    <Button variant="secondary" className="w-full" disabled>Plano Ativo</Button>
                  ) : (
                    <Button 
                      variant="primary" 
                      className="w-full" 
                      onClick={() => handleSubscribe(STRIPE_PRICES.PRO_YEARLY)}
                      loading={processing}
                    >
                      Assinar Agora
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Billing;